import React, { createContext, useContext, useState, useCallback } from 'react';

const LocationContext = createContext();

export const useLocation = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocation must be used within LocationProvider');
  return ctx;
};

// Helper function to create URL-friendly slug from location name
const createLocationSlug = (location) => {
  if (!location) return null;
  const cityName = location.city || location.name || '';
  return cityName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();
};

// Helper function to create restaurant slug from kitchen name
export const createRestaurantSlug = (kitchenName) => {
  if (!kitchenName) return '';
  return kitchenName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export const LocationProvider = ({ children }) => {
  const [selectedLocation, setSelectedLocationState] = useState(() => {
    try {
      const saved = localStorage.getItem('customer_location');
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      // If saved location has no coordinates, drop them so HomePage geocodes fresh
      if (parsed && !parsed.latitude) return { ...parsed, latitude: undefined, longitude: undefined };
      return parsed;
    } catch {
      return null;
    }
  });

  const setSelectedLocation = useCallback((locationOrUpdater) => {
    setSelectedLocationState(prev => {
      const location = typeof locationOrUpdater === 'function'
        ? locationOrUpdater(prev)
        : locationOrUpdater;
      const locationWithSlug = location ? {
        ...location,
        slug: location.slug || createLocationSlug(location),
      } : null;
      if (locationWithSlug) {
        localStorage.setItem('customer_location', JSON.stringify(locationWithSlug));
      } else {
        localStorage.removeItem('customer_location');
      }
      return locationWithSlug;
    });
  }, []);

  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        (err) => reject(err),
        { timeout: 10000 }
      );
    });
  }, []);

  const reverseGeocode = useCallback(async (lat, lon) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`
      );
      const data = await response.json();
      const address = data.address || {};
      return {
        name: data.display_name?.split(',')[0] || 'Current Location',
        fullAddress: data.display_name || '',
        city: address.city || address.town || address.village || address.county || '',
        state: address.state || '',
        country: address.country || '',
        latitude: lat,
        longitude: lon,
      };
    } catch (e) {
      return { name: 'Current Location', latitude: lat, longitude: lon, city: '', fullAddress: '' };
    }
  }, []);

  const geocodeAddress = useCallback(async (address) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=5`
      );
      const data = await response.json();
      return data.map(item => ({
        name: item.display_name?.split(',')[0] || item.display_name,
        fullAddress: item.display_name,
        latitude: parseFloat(item.lat),
        longitude: parseFloat(item.lon),
        placeId: item.place_id?.toString(),
        city: item.address?.city || item.address?.town || item.address?.village || '',
        state: item.address?.state || '',
      }));
    } catch (e) {
      return [];
    }
  }, []);

  return (
    <LocationContext.Provider
      value={{
        selectedLocation,
        setSelectedLocation,
        getCurrentLocation,
        reverseGeocode,
        geocodeAddress,
        createLocationSlug,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
